import React, { useState, useRef, useEffect, useCallback } from 'react'
import zhCN from 'antd/lib/locale/zh_CN'
import {
  ConfigProvider,
  Layout,
  Table,
  Tag,
  Input,
  Popover,
  Select,
  Row,
  Col,
  Pagination,
  Tabs,
} from 'antd'
import { Content } from 'antd/lib/layout/layout'
import {
  SearchOutlined,
  CodeOutlined,
  LoginOutlined,
  LinkOutlined,
} from '@ant-design/icons'
import OAMSider from '../oam-sider'
import LoginRegisterModal from '../../../component/modal/login-register/login-register'
import UserCard from './oam-userinfo-card'
import { sendHttpPostRequest } from '../../../api/http-request'
import { BACKEND_API_URL } from '../../../constants/constants'
import { getRangeDate } from '../../common/utils/util'

import * as echarts from 'echarts/core'
import {
  DatasetComponent,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  ToolboxComponent,
} from 'echarts/components'
import { LineChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

echarts.use([
  TitleComponent,
  ToolboxComponent,
  DatasetComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  CanvasRenderer,
])

const { Option } = Select
const { TabPane } = Tabs

export interface Props {}

export const OAMUserAnalysis: React.FC<Props> = () => {
  // ── 共用时间窗口 ──────────────────────────────────────────────
  const days = useRef(30)
  const startDate = useRef(getRangeDate(-30))
  const endDate = useRef(getRangeDate(0))

  // ── Tab3: 用户行为明细 ─────────────────────────────────────
  const [dataList, setDataList] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const page = useRef(1)
  const pageSize = useRef(20)
  const queryContent = useRef('')

  // ── Tab4: 最近页面访问 ─────────────────────────────────────
  const [visitList, setVisitList] = useState<any[]>([])
  const [visitTotal, setVisitTotal] = useState(0)
  const visitPage = useRef(1)
  const visitPageSize = useRef(20)
  const visitQuery = useRef('')

  // ── 获取趋势数据 ──────────────────────────────────────────────
  const fetchTrend = useCallback(() => {
    sendHttpPostRequest(
      'account/api/query_user_count/',
      { start_time: startDate.current, end_time: endDate.current },
      (data: any) => {
        if (data.code !== -100 && data.code !== -501) {
          renderTrendChart(data)
        }
      }
    )
  }, [])

  const renderTrendChart = (data: any) => {
    const chartDom = document.getElementById('platform-trend-chart')
    if (!chartDom) return
    const myChart = echarts.getInstanceByDom(chartDom) || echarts.init(chartDom)

    // count_list 是对象，key 遍历
    const countList = data.count_list || {}
    const tableData: [string, number][] = [['日期', '新增用户']]
    for (const k in countList) {
      tableData.push([countList[k].date || k, countList[k].count || 0])
    }

    myChart.setOption({
      title: { text: '新增用户趋势', textStyle: { fontWeight: 'normal', fontSize: 14 } },
      legend: { left: 'center', top: 'top' },
      tooltip: { trigger: 'axis', axisPointer: { type: 'line' } },
      dataset: { source: tableData },
      xAxis: { type: 'category', axisLabel: { rotate: 0 } },
      yAxis: { type: 'value' },
      series: [
        {
          type: 'line',
          smooth: true,
          symbol: 'emptyCircle',
          symbolSize: 4,
          color: '#4f46e5',
          lineStyle: { color: '#4f46e5', width: 1.5 },
          areaStyle: { color: '#4f46e5', opacity: 0.4 },
        },
      ],
    })
  }

  // ── 获取用户行为明细 ──────────────────────────────────────
  const fetchUserList = useCallback(() => {
    sendHttpPostRequest(
      'account/api/oam_user_analysis/',
      {
        query: queryContent.current,
        page: page.current,
        pagesize: pageSize.current,
        days: days.current,
      },
      (data: any) => {
        if (data.code === -100 || data.code === -501) {
          window.location.href = '/oam_login'
          return
        }
        setDataList(data.data || [])
        setTotal(data.total || 0)
      }
    )
  }, [])

  // ── 获取最近页面访问 ──────────────────────────────────────
  const fetchRecentVisits = useCallback(() => {
    sendHttpPostRequest(
      'account/api/oam_recent_page_visit/',
      {
        query: visitQuery.current,
        page: visitPage.current,
        pagesize: visitPageSize.current,
        days: days.current,
      },
      (data: any) => {
        if (data.code === -100 || data.code === -501) {
          window.location.href = '/oam_login'
          return
        }
        setVisitList(data.data || [])
        setVisitTotal(data.total || 0)
      }
    )
  }, [])

  useEffect(() => {
    fetchTrend()
    fetchUserList()
  }, [])

  const onDaysChange = (value: string) => {
    const map: Record<string, number> = {
      last7days: 7,
      last15days: 15,
      last30days: 30,
      last90days: 90,
    }
    days.current = map[value] || 30
    startDate.current = getRangeDate(-days.current)
    endDate.current = getRangeDate(0)
    page.current = 1
    visitPage.current = 1
    fetchTrend()
    fetchUserList()
    fetchRecentVisits()
  }

  const onTabChange = (key: string) => {
    if (key === 'users') fetchUserList()
    if (key === 'visits') fetchRecentVisits()
    if (key === 'trend') {
      setTimeout(() => {
        const chartDom = document.getElementById('platform-trend-chart')
        if (chartDom) {
          const inst = echarts.getInstanceByDom(chartDom)
          inst ? inst.resize() : fetchTrend()
        }
      }, 100)
    }
  }

  // ── 搜索 ──────────────────────────────────────────────────
  const onUserSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    queryContent.current = (e.target as HTMLInputElement).value
    page.current = 1
    fetchUserList()
  }
  const onUserSearchClear = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) { queryContent.current = ''; page.current = 1; fetchUserList() }
  }

  const onVisitSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    visitQuery.current = (e.target as HTMLInputElement).value
    visitPage.current = 1
    fetchRecentVisits()
  }
  const onVisitSearchClear = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) { visitQuery.current = ''; visitPage.current = 1; fetchRecentVisits() }
  }

  const onPageChange = (p: number, ps?: number) => {
    page.current = p
    if (ps) pageSize.current = ps
    fetchUserList()
  }
  const onVisitPageChange = (p: number, ps?: number) => {
    visitPage.current = p
    if (ps) visitPageSize.current = ps
    fetchRecentVisits()
  }

  // ── 用户列公共渲染 ─────────────────────────────────────────
  const renderUserCell = (text: string, record: any) => (
    <Popover
      trigger="hover"
      placement="left"
      content={<UserCard user_id={record.user_id} />}
      title={null}
    >
      <Row align="middle" gutter={[8, 0]} style={{ flexWrap: 'nowrap', cursor: 'pointer' }}>
        <Col flex="30px">
          <img
            style={{ width: 28, height: 28, borderRadius: 14 }}
            src={
              record.avatar?.startsWith('http')
                ? record.avatar
                : BACKEND_API_URL + (record.avatar || 'static/avatar/default_avatar.png')
            }
          />
        </Col>
        <Col flex="auto">
          <div style={{ lineHeight: '18px' }}>{text || '-'}</div>
          <div style={{ fontSize: 12, color: '#999', lineHeight: '16px' }}>{record.idens || ''}</div>
        </Col>
      </Row>
    </Popover>
  )

  // ── 用户行为明细列 ─────────────────────────────────────────
  const userColumns = [
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 180,
      render: renderUserCell,
    },
    { title: '单位', dataIndex: 'institute', key: 'institute', width: 160, ellipsis: true },
    { title: '最近登录', dataIndex: 'last_login', key: 'last_login', width: 160 },
    {
      title: '登录次数',
      dataIndex: 'login_count',
      key: 'login_count',
      width: 90,
      sorter: (a: any, b: any) => a.login_count - b.login_count,
      render: (v: number) => <Tag icon={<LoginOutlined />}>{v}</Tag>,
    },
    {
      title: '任务提交',
      dataIndex: 'submit_count',
      key: 'submit_count',
      width: 90,
      sorter: (a: any, b: any) => a.submit_count - b.submit_count,
      render: (v: number) =>
        v > 0 ? <Tag color="blue" icon={<CodeOutlined />}>{v}</Tag> : <span style={{ color: '#ccc' }}>0</span>,
    },
  ]

  // ── 最近访问列 ─────────────────────────────────────────────
  const visitColumns = [
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 180,
      render: renderUserCell,
    },
    {
      title: '访问页面',
      dataIndex: 'page_path',
      key: 'page_path',
      ellipsis: true,
      render: (v: string) => (
        <Tag
          color="geekblue"
          icon={<LinkOutlined />}
          style={{ cursor: 'pointer' }}
          onClick={() => v && window.open(v, '_blank')}
        >
          {v || '/'}
        </Tag>
      ),
    },
    {
      title: '访问次数',
      dataIndex: 'visit_count',
      key: 'visit_count',
      width: 90,
      sorter: (a: any, b: any) => a.visit_count - b.visit_count,
      render: (v: number) => <Tag>{v}</Tag>,
    },
    {
      title: '最近访问时间',
      dataIndex: 'last_visit',
      key: 'last_visit',
      width: 180,
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <OAMSider keys={['user_analysis']} openKeys={['sub-user']} />
      <LoginRegisterModal />

      <Layout style={{ backgroundColor: '#f9f9f9' }}>
        <Content
          style={{
            backgroundColor: '#fff',
            borderRadius: 5,
            minWidth: 1000,
            margin: '30px',
            paddingTop: 20,
            paddingLeft: 30,
            paddingRight: 30,
            paddingBottom: 30,
          }}
        >
          {/* ── 顶部控制栏 ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontWeight: 'normal', fontSize: 18 }}>用户分析</h2>
            <Select defaultValue="last30days" style={{ width: 140 }} onChange={onDaysChange}>
              <Option value="last7days">最近 7 天</Option>
              <Option value="last15days">最近 15 天</Option>
              <Option value="last30days">最近 30 天</Option>
              <Option value="last90days">最近 90 天</Option>
            </Select>
          </div>

          {/* ── 标签页 ── */}
          <Tabs defaultActiveKey="trend" onChange={onTabChange}>

            {/* Tab1: 平台趋势折线图 */}
            <TabPane tab="平台趋势" key="trend">
              <div
                id="platform-trend-chart"
                style={{ width: '100%', height: 380, marginTop: 8 }}
              />
            </TabPane>

            {/* Tab2: 用户行为明细 */}
            <TabPane tab="用户行为明细" key="users">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 12px' }}>
                <Input
                  placeholder="搜索用户名/账号"
                  allowClear
                  suffix={<SearchOutlined style={{ color: '#999' }} />}
                  onPressEnter={onUserSearch}
                  onChange={onUserSearchClear}
                  style={{ width: 260 }}
                />
                <span style={{ color: '#999' }}>共 {total} 位活跃用户</span>
              </div>
              <ConfigProvider locale={zhCN}>
                <Table
                  columns={userColumns}
                  dataSource={dataList}
                  rowKey="user_id"
                  pagination={false}
                  size="middle"
                />
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Pagination
                    current={page.current}
                    pageSize={pageSize.current}
                    total={total}
                    showSizeChanger
                    showQuickJumper
                    onChange={onPageChange}
                  />
                </div>
              </ConfigProvider>
            </TabPane>

            {/* Tab3: 最近访问页面 */}
            <TabPane tab="最近访问" key="visits">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 12px' }}>
                <Input
                  placeholder="搜索用户名/账号/页面路径"
                  allowClear
                  suffix={<SearchOutlined style={{ color: '#999' }} />}
                  onPressEnter={onVisitSearch}
                  onChange={onVisitSearchClear}
                  style={{ width: 300 }}
                />
                <span style={{ color: '#999' }}>共 {visitTotal} 条记录</span>
              </div>
              <ConfigProvider locale={zhCN}>
                <Table
                  columns={visitColumns}
                  dataSource={visitList}
                  rowKey={(record: any) => `${record.user_id}_${record.page_path}`}
                  pagination={false}
                  size="middle"
                />
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Pagination
                    current={visitPage.current}
                    pageSize={visitPageSize.current}
                    total={visitTotal}
                    showSizeChanger
                    showQuickJumper
                    onChange={onVisitPageChange}
                  />
                </div>
              </ConfigProvider>
            </TabPane>

          </Tabs>
        </Content>
      </Layout>
    </Layout>
  )
}

export default OAMUserAnalysis
